/**
 * Os erros tipados da raia eletrônica (E74.F1, ADR-0075): o motivo de cada recusa do servidor, lido
 * do `detail` (código seco ou corpo estruturado com `code`). Saiu de `exame_eletronico.ts` na
 * E136.F3, que o levou ao teto de linhas; o módulo de lá reexporta tudo.
 */
import axios, { type AxiosError } from 'axios';

export type ExameEletronicoErrorReason =
  | 'case_not_found'
  | 'document_not_found'
  | 'not_found'
  | 'candidata_nao_encontrada'
  | 'candidata_ja_confirmada'
  | 'modalidade_invalida'
  | 'sem_protocolo'
  | 'nao_verificado'
  | 'justificativa_requerida'
  | 'concluido'
  | 'consulta_pendente'
  | 'nao_concluido'
  | 'certificacao_requerida'
  | 'protocolo_peca_concluida'
  | 'custodia_interrompida'
  | 'aguardando_arquivos'
  | 'protocolo_nao_importado'
  | 'documento_ilegivel'
  | 'documento_sem_texto'
  | 'arquivo_nao_suportado'
  | 'arquivo_vazio'
  | 'arquivo_muito_grande'
  | 'arquivo_nao_e_imagem'
  | 'conferencia_sem_par'
  | 'conferencia_em_andamento'
  | 'foto_integridade'
  | 'foto_indisponivel'
  | 'conferencia_indisponivel'
  | 'provider_auth'
  | 'provider_credit'
  | 'sem_chave'
  | 'validation'
  | 'forbidden'
  | 'network'
  | 'unknown';

export class ExameEletronicoError extends Error {
  public readonly reason: ExameEletronicoErrorReason;

  constructor(reason: ExameEletronicoErrorReason) {
    super(`exame_eletronico_error:${reason}`);
    this.name = 'ExameEletronicoError';
    this.reason = reason;
  }
}

const HTTP_FORBIDDEN = 403;
const HTTP_NOT_FOUND = 404;
const HTTP_UNPROCESSABLE = 422;

const _DETAIL_TO_REASON: Record<string, ExameEletronicoErrorReason> = {
  CASE_NOT_FOUND: 'case_not_found',
  SOURCE_DOCUMENT_NOT_FOUND: 'document_not_found',
  EXAME_ELETRONICO_NOT_FOUND: 'not_found',
  CASE_MODALIDADE_INVALIDA: 'modalidade_invalida',
  EXAME_ELETRONICO_SEM_PROTOCOLO: 'sem_protocolo',
  EXAME_ELETRONICO_NAO_VERIFICADO: 'nao_verificado',
  EXAME_ELETRONICO_CONCLUIDO: 'concluido',
  // E147.F2.C01: a consulta do sistema da CCB não respondeu e a equipe não registrou a limitação.
  EXAME_ELETRONICO_CONSULTA_PENDENTE: 'consulta_pendente',
  EXAME_ELETRONICO_NAO_CONCLUIDO: 'nao_concluido',
  EXAME_ELETRONICO_JUSTIFICATIVA_REQUERIDA: 'justificativa_requerida',
  EXAME_ELETRONICO_CERTIFICACAO_REQUERIDA: 'certificacao_requerida',
  PROTOCOLO_CASO_PECA_CONCLUIDA: 'protocolo_peca_concluida',
  // E136.F1 (ADR-0092): nenhum documento seguiu; cada um depende de arquivo fora do exame.
  CUSTODIA_INTERROMPIDA: 'custodia_interrompida',
  // E193.F1.C15 (GUS-259 item 3): o caso digital parado; o "Verificar" não roda o motor.
  AGUARDANDO_ARQUIVOS: 'aguardando_arquivos',
  // E77.F1 — recusas da importação do protocolo. Cada uma tem uma frase própria
  // na tela: o que o perito faz a seguir é diferente em cada caso.
  PROTOCOLO_NAO_IMPORTADO: 'protocolo_nao_importado',
  DOCUMENT_UNREADABLE: 'documento_ilegivel',
  DOCUMENT_SEM_TEXTO: 'documento_sem_texto',
  UNSUPPORTED_MIME: 'arquivo_nao_suportado',
  FILE_EMPTY: 'arquivo_vazio',
  FILE_TOO_LARGE: 'arquivo_muito_grande',
  // E80.F1 — recusas do anexo de foto. A extensão e o MIME são do cliente; só
  // abrir os bytes diz se aquilo é uma fotografia (F-E80.F1-02).
  FOTO_IMAGE_EXTENSION_INVALID: 'arquivo_nao_suportado',
  FOTO_IMAGE_MIME_INVALID: 'arquivo_nao_suportado',
  FOTO_IMAGE_EMPTY: 'arquivo_vazio',
  FOTO_IMAGE_TOO_LARGE: 'arquivo_muito_grande',
  FOTO_IMAGE_INVALIDA: 'arquivo_nao_e_imagem',
  FOTO_NAO_ENCONTRADA: 'not_found',
  // E80.F2 — a conferência facial. A recusa da CONTA do provedor tem código
  // próprio porque a ação de quem opera é diferente em cada caso, e
  // "não foi possível" não diria nenhuma das duas (E70.F2).
  CONFERENCIA_SEM_PAR_ELEITO: 'conferencia_sem_par',
  // E200.F1 (GUS-220, ADR-0143 §3): a análise do caso já roda (a automática ou outra da tela); o
  // clique repetido não dispara duas. A tela espera e relê.
  CONFERENCIA_FACIAL_EM_ANDAMENTO: 'conferencia_em_andamento',
  FOTO_INTEGRIDADE_DIVERGENTE: 'foto_integridade',
  // E80.F3.T07 (F-E80.F2-08): ler e conferir são coisas diferentes. Falha de
  // leitura pede nova tentativa; adulteração pede material novo.
  FOTO_INDISPONIVEL: 'foto_indisponivel',
  CONFERENCIA_INDISPONIVEL: 'conferencia_indisponivel',
  CONFERENCIA_PROVIDER_AUTH: 'provider_auth',
  CONFERENCIA_PROVIDER_CREDIT: 'provider_credit',
  USER_HAS_NO_API_KEY: 'sem_chave',
  // E87.F2 — a candidata pedida não está mais no documento (ou nunca esteve).
  // Código próprio porque a ação de quem opera é recarregar a etapa, e não
  // procurar a foto na galeria.
  CANDIDATA_NAO_ENCONTRADA: 'candidata_nao_encontrada',
  // F-E109.F1-03: a candidata a excluir já é foto do caso; a foto sai pela galeria antes.
  CANDIDATA_JA_CONFIRMADA: 'candidata_ja_confirmada',
};

function reasonForStatus(status: number): ExameEletronicoErrorReason {
  if (status === HTTP_FORBIDDEN) return 'forbidden';
  if (status === HTTP_NOT_FOUND) return 'not_found';
  // 422 sem `detail` de domínio = validação de corpo (ex.: hash fora do formato).
  if (status === HTTP_UNPROCESSABLE) return 'validation';
  return 'unknown';
}

export function classifyExameEletronicoError(error: unknown): ExameEletronicoError {
  if (!axios.isAxiosError(error)) return new ExameEletronicoError('unknown');
  const axErr = error as AxiosError<{ detail?: unknown }>;
  if (axErr.response === undefined) return new ExameEletronicoError('network');
  const detail = axErr.response.data?.detail;
  // Dois erros da mesa levam corpo ESTRUTURADO em vez de código seco (os dois
  // graus da reclassificação; quando a certificação foi pedida). Ler só a forma
  // string deixaria a tela cair em "erro inesperado" justamente nos dois casos
  // em que ela tem algo específico a dizer.
  const codigo =
    typeof detail === 'string'
      ? detail
      : typeof detail === 'object' && detail !== null && 'code' in detail
        ? String((detail as { code: unknown }).code)
        : undefined;
  const mapped = codigo === undefined ? undefined : _DETAIL_TO_REASON[codigo];
  if (mapped !== undefined) return new ExameEletronicoError(mapped);
  return new ExameEletronicoError(reasonForStatus(axErr.response.status));
}
